import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FaArrowLeft, FaSearch, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { meetingsConfigurationApi } from '../../../services/api/meetingsConfigurationApi';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, ItemsGrid, ItemCard, ItemCardContent, ItemTitle, ItemDetailsGrid, OrderBadge, ColorDot, ActionButtons, ItalicText } from '../styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';

const MeetingStatuses = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState({ code: '', name: '', description: '', color: '#FF9800', order: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const data = await meetingsConfigurationApi.getAllMeetingStatuses();
      setItems(data);
    } catch (err) {
      showAlert('Failed to load meeting statuses.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setCurrentItem(item);
      setIsEditing(true);
    } else {
      setCurrentItem({ code: '', name: '', description: '', color: '#FF9800', order: items.length + 1 });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (item) => {
    setCurrentItem(item);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem({
      ...currentItem,
      [name]: name === 'order' ? parseInt(value, 10) || 0 : value
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (isEditing) {
        await meetingsConfigurationApi.updateMeetingStatus(currentItem.id, currentItem);
        showAlert('Meeting status updated successfully', 'success');
      } else {
        await meetingsConfigurationApi.createMeetingStatus(currentItem);
        showAlert('Meeting status created successfully', 'success');
      }
      handleCloseDialog();
      await fetchItems();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEditing ? 'update' : 'create'} meeting status.`, 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await meetingsConfigurationApi.deleteMeetingStatus(currentItem.id);
      showAlert('Meeting status deleted successfully', 'success');
      handleCloseDeleteDialog();
      await fetchItems();
    } catch (err) {
      showAlert(err.message || 'Failed to delete meeting status.', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    item.code?.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    item.description?.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  if (loading && items.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/meetings-configuration')}>
            <FaArrowLeft /> Back to Meetings Configuration
          </BackButton>
          <Title>Meeting Statuses Configuration</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={true} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Meeting Statuses"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                  }
                }}
              />
            </Box>

            <HeaderButton
              onClick={() => handleOpenDialog()}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Status
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <div style={{
            color: '#64748B',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontWeight: 600 }}>
              {filteredItems.length}
            </span>
            {filteredItems.length === 1 ? 'status' : 'statuses'} found
          </div>

          {filteredItems.length === 0 ? (
            <EmptyState
              title="No meeting statuses found"
              description={searchTerm ?
                "No meeting statuses match your search criteria." :
                "No meeting statuses have been added yet. Click the '+ Add Status' button to add your first status."}
            />
          ) : (
            <ItemsGrid>
              {filteredItems.map((item) => (
                <ItemCard key={item.id}>
                  <ItemCardContent>
                    <ItemTitle>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ColorDot $color={item.color} />
                        {item.name}
                      </Box>
                      <OrderBadge>
                        Order: {item.order}
                      </OrderBadge>
                    </ItemTitle>
                    <Box sx={{ fontSize: '12px', color: '#94A3B8', mb: 1 }}>
                      Code: {item.code}
                    </Box>
                    <ItemDetailsGrid>
                      {item.description ? item.description : <ItalicText>No description provided</ItalicText>}
                    </ItemDetailsGrid>
                    <ActionButtons>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(item)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ActionButtons>
                  </ItemCardContent>
                </ItemCard>
              ))}
            </ItemsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Meeting Status' : 'Add Meeting Status'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="code"
            label="Code"
            type="text"
            fullWidth
            variant="outlined"
            value={currentItem.code}
            onChange={handleInputChange}
            helperText="Unique identifier (e.g., DRAFT, SCHEDULED, COMPLETED, CANCELLED)"
          />
          <TextField
            margin="dense"
            name="name"
            label="Name"
            type="text"
            fullWidth
            variant="outlined"
            value={currentItem.name}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={currentItem.description}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="color"
            label="Color"
            type="color"
            fullWidth
            variant="outlined"
            value={currentItem.color}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ColorDot $color={currentItem.color} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            name="order"
            label="Display Order"
            type="number"
            fullWidth
            variant="outlined"
            value={currentItem.order}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!currentItem.code?.trim() || !currentItem.name?.trim()}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the meeting status "{currentItem.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MeetingStatuses;
